import { useCallback, useEffect, useRef, useState } from 'react'
import { FormikProvider, useFormik } from 'formik'
import * as yup from 'yup'
import {
  Alert,
  Tab,
  Text,
  Select,
  Dialog,
  Table,
  Button,
  Card,
  CardColors,
  Separator,
  Form,
  Input,
  Loader,
  Tooltip,
} from '@/ui'
import { getFieldNames } from '@/base'
import {
  useTransactionRuleFilters,
  TransactionRuleStatus,
} from './useTransactionRuleFilters'
import { TransactionRuleFilters } from './TransactionRuleFilters'
import { UserSearchDropdown } from '@/components/UserSearchDropdown'
import { CurrencyFormatLabel } from '@/components/CurrencyFormatLabel'
import {
  createTransactionRule,
  archiveTransactionRule,
  restoreTransactionRule,
  CREATE_TRANSACTION_RULE_KEY,
  ARCHIVE_TRANSACTION_RULE_KEY,
  RESTORE_TRANSACTION_RULE_KEY,
  fetchPaginatedTransactionRules,
} from '@/actions/admin/transactionRuleActions'
import { TRANSACTION_TYPE_OPTIONS } from '@/constants/transactionConstants'
import {
  ARCHIVE_REASON_DESCRIPTIONS,
  ARCHIVE_REASON_OPTIONS,
  ArchiveReason,
  RULE_USE_OPTIONS,
  RULE_TYPE_OPTIONS,
} from '@/constants/transactionRuleConstants'
import { centsToDollars, dollarsToCents } from '@/utils/currencyHelpers'
import { getTransactionCategorySelector } from '@/features/reports/reports.selectors'
import { TransactionCategoryDropdown } from '@/components/TransactionCategoryDropdown'
import {
  TransactionRule,
  TransactionRuleCategoryType,
  TransactionRuleType,
  TransactionRuleUse,
} from '@/reducers/admin/transactionRulesReducer'
import { useReselector } from '@/utils/sharedHooks'
import { DATE_FORMATS_LUXON, formatISOFromUTC } from '@/utils/dateHelpers'
import { selectSimilarRules } from '@/features/transactions/transactions.selectors'
import { getFetchError } from '@/reducers/fetch'
import { useAppDispatch } from '@/utils/typeHelpers'
import { TextColors, TypographyStyles } from '@/styles'
import { fetchAdminTransactionCategoriesIfNeeded } from '@/v2-features/reports/reports.slice'

const SimilarRuleItem = ({ rule }: { rule: TransactionRule }) => {
  const selectedCategory = useReselector(
    getTransactionCategorySelector,
    rule.transactionCategoryId
  )
  const applicableUsers = rule.userId ? `User: ${rule.userId}` : 'All Users'

  return (
    <li>
      <p style={{ fontSize: '16px' }}>
        <b>Rule #{rule.id}:</b> Categorizing <b>{rule.ruleText}</b> as{' '}
        <b>{selectedCategory?.name || 'Awaiting Categorization'}</b>, applicable
        to <b>{applicableUsers}</b>
      </p>
    </li>
  )
}
const TransactionRuleCreationModal = ({
  open,
  close,
}: {
  open: boolean
  close: () => void
}) => {
  const dispatch = useAppDispatch()
  const [checkRule, setCheckRule] = useState(false)
  const [ruleUse, setRuleUse] = useState<TransactionRuleUse>()
  const [ruleText, setRuleText] = useState('')
  const [ruleType, setRuleType] = useState<TransactionRuleType>()
  const [amountInCents, setAmountInCents] = useState<string>()
  const [transactionCategoryType, setTransactionCategoryType] =
    useState<TransactionRuleCategoryType>()
  const [transactionCategoryId, setTransactionCategoryId] = useState<number>()
  const [userId, setUserId] = useState<number>()
  const similarRules = useReselector(selectSimilarRules, ruleUse, ruleText)
  const error = useReselector(getFetchError, CREATE_TRANSACTION_RULE_KEY)

  const submit = async () => {
    if (!ruleUse || !ruleType) {
      return
    }

    const result = await createTransactionRule({
      ruleUse,
      ruleText,
      ruleType,
      amountInCents,
      transactionCategoryType,
      transactionCategoryId,
      userId,
    })(dispatch)
    if (result) {
      close()
    }
  }

  const selectedCategory = useReselector(
    getTransactionCategorySelector,
    transactionCategoryId
  )

  const validateRule = () => {
    const ruleFilled = ruleText && ruleType

    if (ruleUse === TransactionRuleUse.CLARIFICATION) {
      return ruleFilled
    } else {
      const categoryFilled =
        transactionCategoryType === 'business'
          ? transactionCategoryType && transactionCategoryId
          : transactionCategoryType

      return ruleFilled && categoryFilled
    }
  }

  const validateAndCheckRule = () => {
    if (validateRule()) {
      setCheckRule(true)
    } else {
      setCheckRule(false)
    }
  }

  const checkRuleContent = () => (
    <>
      <Text variant={TypographyStyles.H2}>Checking New Transaction Rule</Text>
      <Separator />
      <Text variant={TypographyStyles.BODY_MD}>
        Currently checking for similar rules that might conflict with this one.
        Remember, rules are symbol sensitive, and there should only be one rule
        per match word.
      </Text>
      <Card color={CardColors.EMPHASIS}>
        <Text variant={TypographyStyles.BODY_LG}>Your New Rule:</Text>
        <br />
        {ruleUse === TransactionRuleUse.CATEGORIZATION && (
          <Text variant={TypographyStyles.BODY_MD}>
            Categorizing transactions that match <b>{ruleText}</b> with rule
            type <b>{ruleType}</b>
            <br />
            as <b>{selectedCategory?.name || 'Awaiting Categorization'}</b>,
            applicable to <b>{userId ? `User ${userId}` : 'All Users'}</b>
          </Text>
        )}
        {ruleUse === TransactionRuleUse.CLARIFICATION && (
          <Text variant={TypographyStyles.BODY_MD}>
            Mark transactions that match <b>{ruleText}</b> as &#39;Needs
            Clarification&#39;
          </Text>
        )}
      </Card>
      <Card color={CardColors.LEVEL_1}>
        {similarRules.length === 0 && (
          <Text variant={TypographyStyles.BODY_LG}>Rule looks good.</Text>
        )}
        {similarRules.length > 0 && (
          <div>
            <Text variant={TypographyStyles.BODY_LG}>Similar rules:</Text>
            <ul>
              {similarRules.map((rule) => (
                <SimilarRuleItem key={rule.id} rule={rule} />
              ))}
            </ul>
            <Text variant={TypographyStyles.BODY_MD}>
              Are you sure that the new rule you are creating is <b>unique</b>{' '}
              and <b>necessary</b>?
            </Text>
          </div>
        )}
      </Card>
      <Dialog.Footer>
        <Button onClick={close}>Cancel</Button>
        <Button variant="primary" onClick={submit}>
          Create Rule
        </Button>
      </Dialog.Footer>
    </>
  )

  const toggleFormByContent = () => {
    if (ruleUse === TransactionRuleUse.CATEGORIZATION) {
      return (
        <>
          <Separator />
          <div className="flex flex-row">
            <div className="w-1/2 p-2">
              <Text variant={TypographyStyles.BODY_MD}>
                For the following rule:{' '}
              </Text>
              <br />
              <div className="background">
                <Text variant={TypographyStyles.EYEBROW}>
                  Rule Text: Text that we want to match
                </Text>
                <Input
                  placeholder="Enter Exact Text e.g. 'PsychologyToday' "
                  value={ruleText}
                  className="bg-neutral-0"
                  onChange={(e) => setRuleText(e.target.value)}
                />
                <br />
                <br />
                <Text variant={TypographyStyles.EYEBROW}>
                  Rule Type: how we want to match
                </Text>
                <Select
                  placeholder="Enter Text"
                  value={ruleType}
                  options={RULE_TYPE_OPTIONS}
                  onValueChange={(value) => setRuleType(value)}
                />
              </div>
            </div>
            <div className="w-1/2 p-2">
              <Text variant={TypographyStyles.BODY_MD}>
                Categorize transactions that match as:{' '}
              </Text>
              <br />
              <div className="background">
                <Text variant={TypographyStyles.EYEBROW}>
                  Transaction Type{' '}
                </Text>
                <Select
                  placeholder="Select Transaction type"
                  name="transactionCategoryType"
                  value={transactionCategoryType}
                  options={TRANSACTION_TYPE_OPTIONS}
                  onValueChange={(value) => setTransactionCategoryType(value)}
                />
                <br />
                <Text variant={TypographyStyles.EYEBROW}>
                  Transaction Category{' '}
                </Text>
                <TransactionCategoryDropdown
                  optionStyle="combineIdAndValue"
                  onChange={(value) =>
                    setTransactionCategoryId(value || undefined)
                  }
                  value={transactionCategoryId}
                  disabled={false}
                  modal
                />
                <br />
                <br />
                <Text variant={TypographyStyles.EYEBROW}>
                  Applies to: (for all users, leave empty){' '}
                </Text>
                <UserSearchDropdown
                  value={userId}
                  onChange={setUserId}
                  placeholder="Select user or leave empty for all users"
                  modal
                />
                <br />
                <br />
                {/*
                  If user id is set (aka a user rule), we allow the ability to set amounts.
                */}
                {userId && (
                  <>
                    <Text variant={TypographyStyles.EYEBROW}>
                      Amount to match{' '}
                    </Text>
                    {/* Convert to Currency Input when migrated to DSM2 */}
                    <Input
                      className="bg-neutral-0"
                      value={
                        amountInCents
                          ? centsToDollars(amountInCents).toString()
                          : '0'
                      }
                      onChange={(e) =>
                        setAmountInCents(
                          dollarsToCents(e.target.value).toString()
                        )
                      }
                    />
                  </>
                )}
              </div>
            </div>
          </div>
        </>
      )
    } else if (ruleUse === TransactionRuleUse.CLARIFICATION) {
      return (
        <div>
          <Separator />
          <Text variant={TypographyStyles.BODY_MD}>
            For transactions with the following text and match, set as
            &#34;Needs Clarification&#34;
          </Text>
          <br />
          <div>
            <Text variant={TypographyStyles.EYEBROW}>
              Rule Text — Text that we want to match
            </Text>
            <Input
              placeholder="Enter Exact Text e.g. 'PsychologyToday' "
              value={ruleText}
              onChange={(e) => setRuleText(e.target.value)}
              className="bg-neutral-0"
            />
            <br />
            <br />
            <Text variant={TypographyStyles.EYEBROW}>
              Rule Type: how we want to match
            </Text>
            <Select
              placeholder="Enter Text"
              value={ruleType}
              options={RULE_TYPE_OPTIONS}
              onValueChange={(value) => setRuleType(value)}
            />
          </div>
        </div>
      )
    } else {
      return null
    }
  }
  const formContent = () => {
    return (
      <>
        <div>
          <Text variant={TypographyStyles.H3}>
            Create a new Transaction Rule
          </Text>
          {error && <Alert variant="error">{error.message}</Alert>}
          <Alert variant="priority">
            <p>
              <b>Rule Use:</b> This can either be &#39;Clarification&#39; which
              will mark transactions as &#39;Needs Clarification&#39;, or
              &#39;Categorization&#39; which categorizations the transaction.
            </p>
            <p>
              <b>Rule Text:</b> This denotes the String that the bot will use
              for matching. This is symbol sensitive. This means that any spaces
              or commas will also matter.
            </p>
            <p>
              <b>Rule Type:</b> This denotes how the bot will use the Rule Text.{' '}
              <br />
              &#39;EXACT_MATCH&#39; means that the bot will find transactions
              whose description matches the Rule Text <i>exactly.</i>
              <br />
              &#39;CONTAINS_WORD&#39; means that the bot will find transactions
              whose description <i>contains</i> the Rule Text, in a full word.
              i.e. &#34;cat&#34; will match with &#34;cat&#34; but not with
              &#34;category&#34;
            </p>

            <p>
              <b>Category Type:</b> This is what we want to mark the transaction
              type as (business or personal). <br />
            </p>
            <p>
              <b>Category Id:</b> This is what we want to mark the transaction
              category id as. <br />
            </p>

            <p>
              <b>
                Any rule changes or additions will only be applied going
                forward. This means that if you were to update a rule, the
                updated rule will only apply to new transactions.
              </b>
            </p>
          </Alert>
          <Card color={CardColors.EMPHASIS}>
            <Text variant={TypographyStyles.EYEBROW}>Select Rule Use</Text>
            <Select
              placeholder="Select Rule Use"
              name="ruleUse"
              value={ruleUse}
              options={RULE_USE_OPTIONS}
              onValueChange={setRuleUse}
            />

            {toggleFormByContent()}
          </Card>
        </div>
        <Dialog.Footer>
          <Button onClick={close}>Cancel</Button>
          <Button variant="primary" onClick={validateAndCheckRule}>
            Check Rule
          </Button>
        </Dialog.Footer>
      </>
    )
  }
  return (
    <Dialog open={open} onClose={close}>
      <Dialog.Content size="xl">
        {!checkRule && formContent()}
        {checkRule && checkRuleContent()}
      </Dialog.Content>
    </Dialog>
  )
}

const archiveValidationSchema = yup.object({
  reason: yup
    .mixed<ArchiveReason>()
    .oneOf(Object.values(ArchiveReason))
    .required('Please select a reason'),
  description: yup.string().when('reason', {
    is: ArchiveReason.OTHER,
    then: (schema) =>
      schema.trim().required('Please describe why this rule is being archived'),
    otherwise: (schema) => schema,
  }),
})

const TransactionRuleArchiveDialog = ({
  close,
  rule,
  onArchived,
}: {
  close: () => void
  rule: TransactionRule
  onArchived: (archivedRule: TransactionRule) => void
}) => {
  const dispatch = useAppDispatch()
  const error = useReselector(
    getFetchError,
    ARCHIVE_TRANSACTION_RULE_KEY(rule.id)
  )

  const formik = useFormik({
    initialValues: {
      reason: undefined as ArchiveReason | undefined,
      description: '',
    },
    validationSchema: archiveValidationSchema,
    validateOnMount: true,
    onSubmit: async ({ reason, description }) => {
      if (!reason) return
      const isOther = reason === ArchiveReason.OTHER
      const result = await archiveTransactionRule(rule.id, {
        reason,
        ...(isOther ? { description: description.trim() } : {}),
      })(dispatch)
      if (result) {
        onArchived(result)
        close()
      }
    },
  })

  const fieldNames = getFieldNames(formik)
  const selectedReason = formik.values.reason

  return (
    <Dialog open onClose={close}>
      <Dialog.Content>
        <Text variant={TypographyStyles.H2}>Archive Rule #{rule.id}</Text>
        <Separator />
        <Text variant={TypographyStyles.BODY_MD}>Rule you are archiving:</Text>
        {error && <Alert variant="error">{error.message}</Alert>}
        <Card color={CardColors.EMPHASIS}>
          <ul>
            <SimilarRuleItem rule={rule} />
          </ul>
        </Card>
        <FormikProvider value={formik}>
          <div className="mt-4 space-y-3">
            <div>
              <Form.Select
                label="Reason"
                name={fieldNames.reason}
                options={ARCHIVE_REASON_OPTIONS}
                placeholder="Select a reason"
                required
              />
              {selectedReason && (
                <Text
                  variant={TypographyStyles.BODY_XS}
                  color={TextColors.MUTED}
                  className="mt-1"
                >
                  {ARCHIVE_REASON_DESCRIPTIONS[selectedReason]}
                </Text>
              )}
            </div>
            {selectedReason === ArchiveReason.OTHER && (
              <Form.Textarea
                label="Description"
                name={fieldNames.description}
                placeholder="Why is this rule being archived?"
                rows={3}
                shouldClearOnDismount
                required
              />
            )}
          </div>
        </FormikProvider>
        <Dialog.Footer>
          <Button onClick={close}>Cancel</Button>
          <Button
            variant="danger"
            onClick={() => formik.handleSubmit()}
            disabled={!formik.isValid || formik.isSubmitting}
            loading={formik.isSubmitting}
          >
            Archive Rule
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}

const TransactionRuleRestoreDialog = ({
  close,
  rule,
  onRestored,
}: {
  close: () => void
  rule: TransactionRule
  onRestored: (result: {
    restored: TransactionRule
    archived: TransactionRule
  }) => void
}) => {
  const dispatch = useAppDispatch()
  const error = useReselector(
    getFetchError,
    RESTORE_TRANSACTION_RULE_KEY(rule.id)
  )

  const restoreRule = async () => {
    const result = await restoreTransactionRule(rule.id)(dispatch)
    if (result) {
      onRestored(result)
      close()
    }
  }

  return (
    <Dialog open onClose={close}>
      <Dialog.Content>
        <Text variant={TypographyStyles.H2}>Restore Rule #{rule.id}</Text>
        <Separator />
        <Text variant={TypographyStyles.BODY_MD}>
          A new rule will be created with the same settings. The archived rule
          will remain archived for clean audit history.
        </Text>
        {error && <Alert variant="error">{error.message}</Alert>}
        <Card color={CardColors.EMPHASIS}>
          <ul>
            <SimilarRuleItem rule={rule} />
          </ul>
        </Card>
        <Dialog.Footer>
          <Button onClick={close}>Cancel</Button>
          <Button variant="primary" onClick={restoreRule}>
            Restore Rule
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}
const ArchiveReasonCell = ({
  archiveReason,
}: {
  archiveReason: string | null | undefined
}) => {
  if (!archiveReason) return null
  const [reasonEnum, ...rest] = archiveReason.split(' - ')
  const description = rest.join(' - ')
  if (!description) return reasonEnum
  return (
    <Tooltip>
      <Tooltip.Trigger asChild>
        <span className="inline-flex">
          <Text underlined>{reasonEnum}</Text>
        </span>
      </Tooltip.Trigger>
      <Tooltip.Content>{description}</Tooltip.Content>
    </Tooltip>
  )
}

const TransactionRulesRow = ({
  rule,
  status,
  onArchived,
  onRestored,
}: {
  rule: TransactionRule
  status: TransactionRuleStatus
  onArchived: (archivedRule: TransactionRule) => void
  onRestored: (result: {
    restored: TransactionRule
    archived: TransactionRule
  }) => void
}) => {
  const [archiveOpen, setArchiveOpen] = useState(false)
  const [restoreOpen, setRestoreOpen] = useState(false)
  const selectedCategory = useReselector(
    getTransactionCategorySelector,
    rule.transactionCategoryId
  )
  const isArchived = Boolean(rule.archivedAt)
  const restoredAsId = rule.restoredAs?.id ?? null
  const showArchiveColumns = status !== 'active'
  return (
    <Table.Row>
      <Table.Cell>{rule.id}</Table.Cell>
      <Table.Cell>{rule.ruleUse}</Table.Cell>
      <Table.Cell>{rule.ruleText}</Table.Cell>
      <Table.Cell>{rule.ruleType}</Table.Cell>
      <Table.Cell>{rule.transactionCategoryType}</Table.Cell>
      <Table.Cell>
        {selectedCategory
          ? `${selectedCategory.id}: ${selectedCategory?.name}`
          : 'No Category'}
      </Table.Cell>
      <Table.Cell>{rule.userId || 'All Users'}</Table.Cell>
      <Table.Cell>
        {rule.amountInCents !== null ? (
          <CurrencyFormatLabel value={centsToDollars(rule.amountInCents)} />
        ) : (
          'N/A'
        )}
      </Table.Cell>
      {showArchiveColumns && (
        <>
          <Table.Cell>
            {isArchived &&
              formatISOFromUTC(
                rule.archivedAt,
                DATE_FORMATS_LUXON.DISPLAY_SHORT
              )}
          </Table.Cell>
          <Table.Cell>
            <ArchiveReasonCell
              archiveReason={isArchived ? rule.archiveReason : null}
            />
          </Table.Cell>
          <Table.Cell>
            {isArchived &&
            rule.archivedBy !== null &&
            rule.archivedBy !== undefined
              ? `user ${rule.archivedBy}`
              : null}
          </Table.Cell>
        </>
      )}

      <Table.Cell>
        {!isArchived && (
          <Button className="link" onClick={() => setArchiveOpen(true)}>
            Archive Rule
          </Button>
        )}
        {isArchived && restoredAsId === null && (
          <Button className="link" onClick={() => setRestoreOpen(true)}>
            Restore Rule
          </Button>
        )}
        {isArchived && restoredAsId !== null && (
          <Text variant={TypographyStyles.BODY_XS}>
            Restored as #{restoredAsId}
          </Text>
        )}
        {archiveOpen && (
          <TransactionRuleArchiveDialog
            close={() => setArchiveOpen(false)}
            rule={rule}
            onArchived={onArchived}
          />
        )}
        {restoreOpen && (
          <TransactionRuleRestoreDialog
            close={() => setRestoreOpen(false)}
            rule={rule}
            onRestored={onRestored}
          />
        )}
      </Table.Cell>
    </Table.Row>
  )
}

const TransactionRuleTable = ({
  status,
}: {
  status: TransactionRuleStatus
}) => {
  const PAGE_SIZE = 100
  const [transactionRules, setTransactionRules] = useState<TransactionRule[]>(
    []
  )
  const [cursor, setCursor] = useState<string | undefined>(undefined)
  const [isFetching, setIsFetching] = useState(false)
  const sentinelRef = useRef<HTMLTableRowElement>(null)
  const requestIdRef = useRef(0)

  const showArchiveColumns = status !== 'active'
  // 9 base columns (Id..For amount, Action) + 3 archive columns when shown
  const columnCount = showArchiveColumns ? 12 : 9

  // Filter hook
  const {
    filters,
    filterInputs,
    buildFilter,
    applyFilters,
    clearFilters,
    updateFilterInput,
    hasActiveFilters,
  } = useTransactionRuleFilters(status)

  const handleArchived = useCallback(
    (archivedRule: TransactionRule) => {
      setTransactionRules((prev) =>
        status === 'active'
          ? prev.filter((r) => r.id !== archivedRule.id)
          : prev.map((r) => (r.id === archivedRule.id ? archivedRule : r))
      )
    },
    [status]
  )

  const handleRestored = useCallback(
    ({
      restored,
      archived,
    }: {
      restored: TransactionRule
      archived: TransactionRule
    }) => {
      setTransactionRules((prev) => {
        const updated = prev.map((r) => (r.id === archived.id ? archived : r))
        if (status === 'archived') return updated
        // Active and All tabs both should surface the new active rule.
        return [restored, ...updated]
      })
    },
    [status]
  )

  const getNextPage = useCallback(async () => {
    if (!isFetching && cursor) {
      setIsFetching(true)
      try {
        const res = await fetchPaginatedTransactionRules({
          filter: buildFilter(),
          pagination: { take: PAGE_SIZE, cursor },
        })
        setTransactionRules((prev) => [...prev, ...res.data])
        setCursor(res.pagination.nextCursor)
      } finally {
        setIsFetching(false)
      }
    }
  }, [buildFilter, cursor, isFetching])

  useEffect(() => {
    const currentRequestId = ++requestIdRef.current
    const fetchInitialPage = async () => {
      setIsFetching(true)
      try {
        const res = await fetchPaginatedTransactionRules({
          filter: buildFilter(),
          pagination: { take: PAGE_SIZE },
        })
        // Only update state if this is still the latest request
        if (currentRequestId === requestIdRef.current) {
          setTransactionRules(res.data)
          setCursor(res.pagination.nextCursor)
        }
      } finally {
        if (currentRequestId === requestIdRef.current) {
          setIsFetching(false)
        }
      }
    }
    fetchInitialPage()
  }, [status, filters, buildFilter])

  // Set up IntersectionObserver for infinite scroll
  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel) return undefined

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && cursor && !isFetching) {
          getNextPage()
        }
      },
      {
        // rootMargin: bottom value of 50% triggers when sentinel is 50% viewport height away
        rootMargin: '0px 0px 50% 0px',
        threshold: 0,
      }
    )

    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [cursor, isFetching, getNextPage])

  return (
    <div>
      <div className="mb-4 flex justify-end">
        <TransactionRuleFilters
          filterInputs={filterInputs}
          hasActiveFilters={hasActiveFilters}
          isLoading={isFetching}
          onUpdateFilterInput={updateFilterInput}
          onApplyFilters={applyFilters}
          onClearFilters={clearFilters}
        />
      </div>

      <div className="overflow-x-auto">
        <Table compact="very">
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Id</Table.HeaderCell>
              <Table.HeaderCell>Rule Use</Table.HeaderCell>
              <Table.HeaderCell>Rule Text</Table.HeaderCell>
              <Table.HeaderCell>Rule Type</Table.HeaderCell>
              <Table.HeaderCell>Category Type</Table.HeaderCell>
              <Table.HeaderCell>Category Id</Table.HeaderCell>
              <Table.HeaderCell>Applies to:</Table.HeaderCell>
              <Table.HeaderCell>For amount:</Table.HeaderCell>
              {showArchiveColumns && (
                <>
                  <Table.HeaderCell>Archived At</Table.HeaderCell>
                  <Table.HeaderCell>Reason</Table.HeaderCell>
                  <Table.HeaderCell>Archived By</Table.HeaderCell>
                </>
              )}
              <Table.HeaderCell>Action</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {transactionRules.map((rule) => (
              <TransactionRulesRow
                rule={rule}
                key={rule.id}
                status={status}
                onArchived={handleArchived}
                onRestored={handleRestored}
              />
            ))}
            {/* Sentinel row at end to trigger next page fetch */}
            {cursor && (
              <tr ref={sentinelRef} style={{ height: 1 }}>
                <td colSpan={columnCount} />
              </tr>
            )}
            {isFetching && (
              <Table.Row>
                <Table.Cell colSpan={columnCount} className="text-center py-4">
                  <Loader />
                </Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
        </Table>
      </div>
    </div>
  )
}
const TransactionRulesPanel = () => {
  const [open, setOpen] = useState(false)
  const dispatch = useAppDispatch()
  useEffect(() => {
    dispatch(fetchAdminTransactionCategoriesIfNeeded())
  }, [dispatch])

  return (
    <div id="autocategorizationPanel">
      <Text variant={TypographyStyles.H1}>
        Autocategorization Transaction Rules
      </Text>
      <br />

      <Alert>
        <Text variant={TypographyStyles.H2}>
          Welcome to the Autocategorization Rules page!
        </Text>
        This is an critical piece of how we process our transactions. Please
        don&#39;t use this tool until you have been trained by Victoria.
      </Alert>
      <br />
      <Alert variant="priority">
        <Text variant={TypographyStyles.H3} className="mb-2">
          How Auto Categorization Works:
        </Text>
        <Text variant={TypographyStyles.BODY_SM} className="mb-1">
          Our bot uses Transaction Rules to determine how to match certain
          transactions.{' '}
        </Text>
        <Text variant={TypographyStyles.BODY_SM} className="mb-1">
          <b>Rule Use:</b> This can either be &#39;Clarification&#39; which will
          mark transactions as &#39;Needs Clarification&#39;, or
          &#39;Categorization&#39; which categorizations the transaction.
        </Text>
        <Text variant={TypographyStyles.BODY_SM} className="mb-1">
          <b>Rule Text:</b> This denotes the String that the bot will use for
          matching. This is symbol sensitive. This means that any spaces or
          commas will also matter.
        </Text>
        <Text variant={TypographyStyles.BODY_SM} className="mb-1">
          <b>Rule Type:</b> This denotes how the bot will use the Rule Text.{' '}
          <br />
          &#39;EXACT_MATCH&#39; means that the bot will find transactions whose
          description matches the Rule Text <i>exactly.</i>
          <br />
          &#39;CONTAINS_WORD&#39; means that the bot will find transactions
          whose description <i>contains</i> the Rule Text, in a full word. i.e.
          &#34;cat&#34; will match with &#34;cat&#34; but not with
          &#34;category&#34;
        </Text>

        <Text variant={TypographyStyles.BODY_SM} className="mb-1">
          <b>Category Type:</b> This is what we want to mark the transaction
          type as (business or personal). <br />
        </Text>
        <Text variant={TypographyStyles.BODY_SM} className="mb-1">
          <b>Category Id:</b> This is what we want to mark the transaction
          category id as. <br />
        </Text>

        <Text variant={TypographyStyles.BODY_SM} className="mb-1">
          <b>
            Any rule changes or additions will only be applied going forward.
            This means that if you were to update a rule, the updated rule will
            only apply to new transactions.
          </b>
        </Text>
      </Alert>
      <br />
      <Button
        variant="primary"
        size="sm"
        onClick={() => setOpen(true)}
        className="mt-4"
      >
        Create New Transaction Rule
      </Button>
      {open && (
        <TransactionRuleCreationModal
          close={() => setOpen(false)}
          open={open}
        />
      )}
      <br />
      <br />
      <Tab
        defaultActiveIndex={0}
        panes={[
          {
            menuItem: 'Active',
            render: () => (
              <>
                <br />
                <Text variant={TypographyStyles.H3}>Active Rules</Text>
                <TransactionRuleTable status="active" />
              </>
            ),
          },
          {
            menuItem: 'Archived',
            render: () => (
              <>
                <br />
                <Text variant={TypographyStyles.H3}>Archived Rules</Text>
                <TransactionRuleTable status="archived" />
              </>
            ),
          },
          {
            menuItem: 'All',
            render: () => (
              <>
                <br />
                <Text variant={TypographyStyles.H3}>All Rules</Text>
                <TransactionRuleTable status="all" />
              </>
            ),
          },
        ]}
      />
    </div>
  )
}

export default TransactionRulesPanel
